import React, { useState, useRef, useEffect, useCallback } from 'react';
import PostHeader from './PostHeader';
import PostContent from './PostContent';
import PostMediaCollage from './PostMediaCollage';
import PostActions from './PostActions';
import PostTags from './PostTags';
import './FeedPost.css';
import './PostHeader.css';

// Shared intersection observer for better performance
// No debounce — IntersectionObserver already batches entries internally.
// Debouncing on top was causing dropped visibility events during rapid scroll/refresh.
let sharedObserver = null;
const observedElements = new Map();

const getSharedObserver = () => {
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const callback = observedElements.get(entry.target);
          if (callback) {
            callback(entry.isIntersecting);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '100px',
      }
    );
  }
  return sharedObserver;
};

const FeedPost = React.memo(({ post, onMediaClick, isDarkMode, compact = false }) => {
  // Sticky visibility: once true, stays true forever.
  // No benefit to unloading images that are already in browser cache,
  // and flapping src="" causes blank spaces.
  const [isVisible, setIsVisible] = useState(false);
  const postRef = useRef();

  const handleVisibilityChange = useCallback((intersecting) => {
    if (intersecting) {
      setIsVisible(true);
    }
    // Never set back to false — sticky visibility
  }, []);

  // Intersection observer with shared instance
  useEffect(() => {
    if (!postRef.current) return;

    const observer = getSharedObserver();
    const element = postRef.current;
    
    // Store callback for this element
    observedElements.set(element, handleVisibilityChange);
    observer.observe(element);

    return () => {
      observedElements.delete(element);
      observer.unobserve(element);
    };
  }, [handleVisibilityChange]);

  const handleMediaClick = (mediaIndex) => {
    onMediaClick(post, mediaIndex);
  };

  return (
    <article 
      className={`feed-post ${isDarkMode ? 'dark' : 'light'}`}
      ref={postRef}
      data-post-id={post.id}
    >
      {!compact && <PostHeader post={post} isDarkMode={isDarkMode} />}
      
      <PostContent 
        content={post.content}
        title={post.title}
        isDarkMode={isDarkMode}
        compact={compact}
      />
      
      {/* Always render PostMediaCollage, control visibility with opacity */}
      {post.media_files && post.media_files.length > 0 && (
        <PostMediaCollage
          mediaFiles={post.media_files}
          onMediaClick={handleMediaClick}
          postId={post.id}
          isDarkMode={isDarkMode}
          isVisible={isVisible}
          style={{
            opacity: isVisible ? 1 : 0,
            visibility: isVisible ? 'visible' : 'hidden',
            transition: 'opacity 0.3s ease'
          }}
        />
      )}
      
      {/* Tags temporarily hidden
      {post.tags && post.tags.length > 0 && (
        <PostTags tags={post.tags} isDarkMode={isDarkMode} />
      )}
      */}
      
      <PostActions 
        post={post}
        isDarkMode={isDarkMode}
        compact={compact}
      />
    </article>
  );
});

FeedPost.displayName = 'FeedPost';

export default FeedPost;
